import { Component, inject, OnInit } from '@angular/core';
import { UserService } from '../../../../services/user/user.service';
import { map } from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIconButton } from '@angular/material/button';
import { AsyncPipe } from '@angular/common';
import { Settings, SettingsOriginator, SettingsService } from '../../../../services/settings/settings.service';
import { SnackbarService } from '../../../../services/snackbar/snackbar.service';
import { MediaService } from '../../../../services/media/media.service';
import { MatrixService } from '../../../../services/matrix/matrix.service';
import { PresenceService, PresenceStatus } from '../../../../services/presence/presence.service';
import { ProfilSettingsTestIds } from './profil-settings.test-ids';

@Component({
  selector: 'app-profil-settings',
  imports: [MatIcon, MatTooltip, MatIconButton, AsyncPipe],
  templateUrl: './profil-settings.component.html',
  styleUrl: './profil-settings.component.scss'
})
/**
 * Die Profileinstellungen für den User.
 * Begründung: Es werden keine AFOs durch die Möglichkeit der Profileinstellungen erfüllt.
 */
export class ProfilSettingsComponent implements OnInit {
  userid: string | null = null;
  userDisplayName = '';
  public avatarFile: File | null = null;
  public readonly userService = inject(UserService);
  public settingsOriginator: SettingsOriginator = new SettingsOriginator(Settings.getInstance());
  protected readonly testIds = ProfilSettingsTestIds;
  protected readonly clientService = inject(MatrixService);
  private readonly mediaService = inject(MediaService);
  private readonly settingsService = inject(SettingsService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly presenceService = inject(PresenceService);
  presenceStatus$ = this.presenceService.getPresence(this.clientService.getUserId() ?? '').pipe(
    map((response: PresenceStatus) => {
      return response === 'online' ? 'online' : 'offline';
    })
  );

  ngOnInit(): void {
    this.userid = this.clientService.client.getUserId();
    const displayName = this.clientService.getUser(this.userid!)?.displayName;
    this.userDisplayName = displayName ?? 'Unknown';

    if (this.settingsOriginator.state.settingsConfigurations.avatarIcon) {
      this.userService.setUserData(this.userid!, this.getAvatarUrl());
    }
  }

  async chooseAvatar(event: Event): Promise<void> {
    const selectedFile: File | null = await this.mediaService.getAndCheckAvatarFileOfEvent(event);
    if (selectedFile) {
      this.avatarFile = selectedFile;
    }
    await this.saveSettings();
  }

  async saveSettings(): Promise<void> {
    const userId = this.clientService.client.getUserId();
    if (this.avatarFile) {
      const xml = globalThis.XMLHttpRequest;
      try {
        const res = await this.mediaService.uploadAvatarToServer(this.avatarFile);
        globalThis.XMLHttpRequest = xml;
        this.settingsOriginator.state.settingsConfigurations.avatarIcon = res.content_uri;
        await this.settingsService.saveSettings(this.clientService.client, this.settingsOriginator.state);
        await this.presenceService.updateCurrentUserPresence();
        await this.clientService.client.setAvatarUrl(res.content_uri);
        this.snackbarService.success('Änderungen gespeichert');
      } catch (err: any) {
        this.snackbarService.error('Die Settings konnten nicht gespeichert werden');
        console.error('Could not save Settings', err);
      }
      if (userId) {
        this.userService.setUserData(userId, this.userService.getBlobUrl(this.avatarFile));
      }
    } else if (this.settingsOriginator.state.settingsConfigurations.avatarIcon === '') {
      await this.settingsService.saveSettings(this.clientService.client, this.settingsOriginator.state);
      await this.presenceService.updateCurrentUserPresence();
      if (userId) {
        this.userService.setUserData(userId, '');
      }
      this.snackbarService.success('Änderungen gespeichert');
    }
  }

  public getURLforFile(file: File): string {
    return URL.createObjectURL(file);
  }

  public async deleteUserAvatar(): Promise<void> {
    this.userService.deleteUserAvatar();
    this.settingsOriginator.state.settingsConfigurations.avatarIcon = '';
    this.avatarFile = null;
    await this.clientService.client.setAvatarUrl('');
    await this.saveSettings();
  }

  copyUserId(): void {
    if (this.userid) {
      navigator.clipboard
        .writeText(this.userid)
        .then(() => {
          this.snackbarService.success('TIM-Adresse kopiert');
        })
        .catch(err => {
          console.error('Failed to copy: ', err);
        });
    }
  }

  public getAvatarUrl(): string {
    const avatarIcon = this.settingsOriginator.state.settingsConfigurations.avatarIcon;
    if (!avatarIcon) return '';
    return this.clientService.client.mxcUrlToHttp(avatarIcon) || '';
  }
}
